import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Phone, ExternalLink, Trash2, Plus, ShieldAlert } from "lucide-react";
import { COUNTRIES } from "@/constants/countries";

export default function NecesitoApoyo() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [reasons, setReasons] = useState([]);
  const [text, setText] = useState("");
  const [helplines, setHelplines] = useState([]);
  const [verified, setVerified] = useState(true);
  const [countryCode, setCountryCode] = useState("CL");

  const load = () => api.get("/reasons").then((r)=>setReasons(r.data));
  useEffect(() => {
    load();
    const uc = (user?.country || user?.location?.country || "CL").toUpperCase();
    setCountryCode(uc);
    api.get("/geo/helplines", { params: { country: uc } })
      .then((r) => {
        const data = r.data || {};
        // New response shape: {country, verified, helplines}. Fall back to legacy array shape.
        if (Array.isArray(data)) {
          setHelplines(data);
          setVerified(true);
        } else {
          setHelplines(data.helplines || []);
          setVerified(!!data.verified);
        }
      })
      .catch(() => { setHelplines([]); setVerified(false); });
    // Anonymous counter — never sends user_id. Backend only stores date+count.
    api.post("/support-page/view").catch(() => {});
  }, [user?.country, user?.location?.country]);

  const add = async () => {
    if (!text.trim()) return;
    try { await api.post("/reasons", { text }); setText(""); await load(); }
    catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/reasons/${id}`); await load(); };

  const HELPLINE_COLORS = {
    apoyo:    "bg-[#38BDF8]/15 border-[#38BDF8]/40 hover:bg-[#38BDF8]/25 text-[#38BDF8]",
    amistad:  "bg-[#FBBF24]/15 border-[#FBBF24]/40 hover:bg-[#FBBF24]/25 text-[#FBBF24]",
    primary:  "bg-[#FF6B5E]/15 border-[#FF6B5E]/40 hover:bg-[#FF6B5E]/25 text-[#FF6B5E]",
    neutral:  "bg-white/5 border-white/10 hover:bg-white/10 text-white/70",
  };
  const countryName = COUNTRIES.find((c) => c.code === countryCode)?.name || countryCode;

  return (
    <div className="mx-auto max-w-md px-4 pt-6">
      <button onClick={()=>nav(-1)} className="flex items-center gap-1 text-[#C7CBD6] mb-3"><ArrowLeft size={18} strokeWidth={1.9}/> Volver</button>
      <h1 className="font-display text-[20px] font-black">Necesito apoyo</h1>
      <p className="text-[#C7CBD6] text-sm mt-2">Un lugar tranquilo para respirar, recordar tus razones y pedir ayuda.</p>

      {/* Breathing */}
      <section className="mt-6 ps-card p-6 relative overflow-hidden">
        <p className="ps-lab"><Plus size={12} strokeWidth={1.9}/> Respiración guiada</p>
        <p className="text-sm mt-1 text-[#C7CBD6]">4s inhala · 4s mantén · 6s exhala. Cierra los ojos si quieres.</p>
        <BreathingCircle/>
      </section>

      {/* Mis razones */}
      <section className="mt-5 ps-card p-5">
        <p className="ps-lab"><Plus size={12} strokeWidth={1.9}/> Mis razones</p>
        <p className="text-sm text-[#C7CBD6] mt-1">Escribe frases personales de por qué vives sin consumo. Vuelve a leerlas cuando lo necesites.</p>
        <div className="mt-3 flex gap-2">
          <input data-testid="reason-input" className="ps-input flex-1" placeholder="Ej: quiero verme crecer con calma." value={text} onChange={(e)=>setText(e.target.value)}/>
          <button data-testid="reason-add" onClick={add} className="ps-btn-primary px-4"><Plus size={16} strokeWidth={1.9}/></button>
        </div>
        <div className="mt-4 space-y-2">
          {reasons.map((r) => (
            <div key={r.id} className="flex items-start gap-2 px-3 py-2 rounded-2xl bg-white/5 border border-white/[.16]">
              <p className="flex-1 text-sm text-white">{r.text}</p>
              <button data-testid={`reason-del-${r.id}`} onClick={()=>del(r.id)} className="text-[#8E93A3] hover:text-white"><Trash2 size={14} strokeWidth={1.9}/></button>
            </div>
          ))}
          {reasons.length === 0 && <p className="text-xs text-[#8E93A3]">Aún no has escrito ninguna. Empieza por una pequeña.</p>}
        </div>
      </section>

      {/* Telefonos — helplines por país; muestra fallback seguro si no hay verificadas */}
      <section className="mt-5 space-y-2" data-testid="helplines-section">
        <p className="ps-lab"><Phone size={12} strokeWidth={1.9}/> Teléfonos de ayuda en {countryName}</p>
        {!verified && (
          <div className="rounded-2xl border border-[#FBBF24]/40 bg-[#FBBF24]/10 p-4" data-testid="helplines-fallback">
            <div className="flex items-start gap-3">
              <ShieldAlert size={20} strokeWidth={1.9} className="text-[#FBBF24] shrink-0 mt-0.5"/>
              <div>
                <p className="text-sm text-white font-semibold">Estamos verificando las líneas de ayuda de {countryName}.</p>
                <p className="text-xs text-[#C7CBD6] mt-1.5 leading-relaxed">
                  Si es una emergencia, marca el número de emergencias local. Aquí tienes orientación profesional:
                </p>
                <a
                  href="https://sinadicciones.org"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="link-sinadicciones"
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 hover:bg-white/15 px-4 py-2 text-sm text-white transition"
                >
                  <ExternalLink size={14} strokeWidth={1.9}/> Ir a SinAdicciones.org
                </a>
              </div>
            </div>
          </div>
        )}
        {verified && helplines.map((h, i) => {
          const cls = HELPLINE_COLORS[h.color] || HELPLINE_COLORS.neutral;
          const isLink = !!h.url;
          const href = isLink ? h.url : `tel:${h.phone}`;
          const TESTID_MAP = { "Salud Responde": "tel-salud", "Prevención del suicidio": "tel-suicidio", "Urgencias": "tel-urgencias", "Orientación profesional": "link-sinadicciones" };
          const testId = TESTID_MAP[h.name] || (isLink ? "helpline-link" : "helpline-tel");
          const HelplineIcon = isLink ? ExternalLink : Phone;
          return (
            <a
              key={h.name || i}
              data-testid={testId}
              href={href}
              {...(isLink ? { target: "_blank", rel: "noreferrer" } : {})}
              className={`flex items-center gap-3 px-4 py-4 rounded-2xl border transition ${cls}`}
            >
              <HelplineIcon size={20} strokeWidth={1.9} className={cls.split(" ").find((c) => c.startsWith("text-")) || "text-white"}/>
              <div className="flex-1">
                <p className="font-display font-bold text-white">{h.name}</p>
                <p className="text-sm text-[#C7CBD6]">{h.display}</p>
              </div>
            </a>
          );
        })}
      </section>

      <p className="mt-6 text-xs text-white/40 text-center">PlanSobrio no reemplaza tratamiento profesional ni atención de urgencia.</p>
    </div>
  );
}

function BreathingCircle() {
  // 4-4-6 pattern, cycle 14s
  return (
    <div className="mt-4 flex flex-col items-center py-4">
      <div className="relative w-52 h-52 flex items-center justify-center">
        <motion.div
          className="absolute w-full h-full rounded-full"
          style={{ background: "radial-gradient(circle, rgba(56,189,248,0.4), rgba(139,92,246,0.15) 70%, transparent)" }}
          animate={{ scale: [0.7, 1.15, 1.15, 0.7], opacity: [0.6, 1, 1, 0.6] }}
          transition={{ duration: 14, ease: "easeInOut", repeat: Infinity, times: [0, 0.286, 0.571, 1] }}
        />
        <motion.div
          className="w-32 h-32 rounded-full ps-gradient"
          animate={{ scale: [0.85, 1.1, 1.1, 0.85] }}
          transition={{ duration: 14, ease: "easeInOut", repeat: Infinity, times: [0, 0.286, 0.571, 1] }}
        />
        <motion.p
          className="absolute font-display font-black text-lg"
          animate={{ opacity: [1, 1, 1, 1] }}
        >
          <motion.span
            animate={{ opacity: [0, 1, 0, 0, 0], }}
            transition={{ duration: 14, repeat: Infinity, times: [0, 0.14, 0.286, 0.6, 1] }}
            className="absolute inset-0 flex items-center justify-center"
          >Inhala</motion.span>
          <motion.span
            animate={{ opacity: [0, 0, 1, 1, 0] }}
            transition={{ duration: 14, repeat: Infinity, times: [0, 0.286, 0.32, 0.571, 0.6] }}
            className="absolute inset-0 flex items-center justify-center"
          >Mantén</motion.span>
          <motion.span
            animate={{ opacity: [0, 0, 0, 1, 0] }}
            transition={{ duration: 14, repeat: Infinity, times: [0, 0.571, 0.6, 0.9, 1] }}
            className="absolute inset-0 flex items-center justify-center"
          >Exhala</motion.span>
          <span className="opacity-0">Mantén</span>
        </motion.p>
      </div>
      <p className="text-xs text-white/40 mt-4">Repite por 2 minutos.</p>
    </div>
  );
}
